import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl, FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/enterprise/api';
import PageHeader from 'common/components/PageHeader';

import DirectAddModal from './DirectAddModal';
import InviteModal from './InviteModal';
import UserManagement from './UserManagement';
import { reportAddExistingUser, reportInviteUser, reportRemoveAccess } from './behavioral';
import { telemetryUserManagementClickSendInvitationInteraction } from './telemetry';
import { AccessDescription } from './types';
import { parseApiError } from './utils';
import { useEnterpriseContext } from '../enterprise/context/EnterpriseContext';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const EnterpriseUserManagement = () => {
  const { url } = useRouteMatch();
  const intl = useIntl();
  const { enterprise } = useEnterpriseContext();
  const enterpriseId = enterprise?.id ?? '';

  const {
    data: roles = null,
    error,
    refetch,
  } = useQuery<AccessDescription[], AxiosError>(
    [`/v1/merchant-admin/enterprises/${enterprise?.id}/access`],
    () => api.fetchEnterpriseAccess(enterpriseId),
    {
      enabled: !!enterpriseId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const removeAccessMutation = useMutation(
    (user: AccessDescription) => api.deleteEnterpriseAccess(enterpriseId, user.email, user.id),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error: AxiosError) => {
        toast.error(<FormattedMessage id={parseApiError(error)} />, { duration: Infinity });
      },
    },
  );

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const handleInviteSuccess = () => {
    telemetryUserManagementClickSendInvitationInteraction();
    setIsInviteModalOpen(false);
    refetch();
    reportInviteUser('enterprise');
  };

  const handleDirectAddSuccess = () => {
    telemetryUserManagementClickSendInvitationInteraction();
    setIsAddUserModalOpen(false);
    refetch();
    reportAddExistingUser('enterprise');
  };

  const handleRemoveAccess = async (user: AccessDescription) => {
    await removeAccessMutation.mutateAsync(user);
    reportRemoveAccess('enterprise');
  };

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.access-management' })} />
      <Switch>
        <Route path={`${url}/user-management`}>
          <UserManagement
            entityId={enterpriseId}
            entityType="enterprise"
            errorReason={error ? parseApiError(error) : null}
            isRemovingAccess={removeAccessMutation.isLoading}
            // TODO: integrate enterprise venues
            isRetailContext={false}
            onAddUser={() => setIsAddUserModalOpen(true)}
            onInviteUser={() => setIsInviteModalOpen(true)}
            onRemoveAccess={handleRemoveAccess}
            roles={roles}
          />
        </Route>
        <Redirect from={url} exact to={`${url}/user-management`} />
      </Switch>
      <InviteModal
        entityId={enterpriseId}
        entityType={'enterprise'}
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
      <DirectAddModal
        entityId={enterpriseId}
        entityType={'enterprise'}
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={handleDirectAddSuccess}
      />
    </Root>
  );
};

export default EnterpriseUserManagement;
